import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Check, Crown, Zap, Sparkles, Building2, Download, FileText, CheckCircle2, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSupportContact } from "../context/SupportContactContext";
import { toast } from "sonner";
import { BackButton } from "../components/BackButton";


const ICONS = {
  slate: Building2,
  blue: Zap,
  orange: Crown,
  indigo: Sparkles,
  emerald: Sparkles,
  rose: Crown,
};

const COLOR_MAP = {
  slate: { ring: "border-slate-200", btn: "bg-slate-100 text-slate-700", badge: "bg-slate-700 text-white", icon: "text-slate-700" },
  blue: { ring: "border-blue-300 ring-2 ring-blue-100", btn: "bg-blue-800 text-white hover:bg-blue-900", badge: "bg-blue-800 text-white", icon: "text-blue-700" },
  orange: { ring: "border-orange-300 ring-2 ring-orange-100", btn: "bg-orange-600 text-white hover:bg-orange-700", badge: "bg-orange-600 text-white", icon: "text-orange-600" },
  indigo: { ring: "border-indigo-200", btn: "bg-indigo-700 text-white hover:bg-indigo-800", badge: "bg-indigo-700 text-white", icon: "text-indigo-700" },
  emerald: { ring: "border-emerald-200", btn: "bg-emerald-700 text-white hover:bg-emerald-800", badge: "bg-emerald-700 text-white", icon: "text-emerald-700" },
  rose: { ring: "border-rose-200", btn: "bg-rose-700 text-white hover:bg-rose-800", badge: "bg-rose-700 text-white", icon: "text-rose-700" },
};

export default function PricingPage() {
  const { user } = useAuth();
  const { supportContact } = useSupportContact();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [invoiceModal, setInvoiceModal] = useState({
    open: false,
    invoiceNumber: "",
    downloadUrl: "",
    planName: "",
  });

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => { });
  }, []);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const choose = async (p) => {
    if (!user) { toast.error("Please sign in to subscribe"); return; }
    if (p.monthly_price === 0 && p.name === "Enterprise") { toast.message("Talk to sales: sales@iip.com"); return; }

    const price = billing === "yearly" ? p.yearly_price : p.monthly_price;
    if (price === 0) {
      try {
        const verifyRes = await api.post("/payments/verify", {
          razorpay_payment_id: "free_payment",
          razorpay_order_id: "free_plan",
          razorpay_signature: "free_signature",
          plan_id: p.id,
          billing_cycle: billing
        });
        if (verifyRes.data.ok) {
          toast.success("Subscribed to Free plan successfully!");
          window.location.reload();
        }
      } catch (e) {
        toast.error("Failed to switch plan.");
      }
      return;
    }

    const loaded = await loadRazorpayScript();
    if (!loaded) {
      toast.error("Failed to load Razorpay payment gateway.");
      return;
    }

    try {
      const orderRes = await api.post("/payments/create-order", {
        plan_id: p.id,
        billing_cycle: billing
      });
      const orderData = orderRes.data;

      const basePrice = billing === "yearly" ? p.yearly_price : p.monthly_price;
      const gstAmount = Math.round(basePrice * 0.18);
      const totalPrice = basePrice + gstAmount;

      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Indian Industrial Platform",
        description: `${p.name} Plan (₹${basePrice} + ₹${gstAmount} 18% GST = ₹${totalPrice})`,
        order_id: orderData.order_id,
        handler: async function (response) {
          try {
            const verifyRes = await api.post("/payments/verify", {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: p.id,
              billing_cycle: billing
            });
            if (verifyRes.data.ok) {
              toast.success(`Successfully subscribed to ${p.name}!`);
              setInvoiceModal({
                open: true,
                invoiceNumber: verifyRes.data.invoice_number,
                downloadUrl: verifyRes.data.download_url,
                planName: p.name,
              });
            }
          } catch (e) {
            toast.error("Payment verification failed.");
          }
        },
        prefill: {
          name: user?.name || "Subscriber",
          email: user?.email || "",
          contact: user?.mobile || supportContact.support_phone || "",
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled.");
          },
        },
        theme: {
          color: "#1e3a8a"
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        const reason = response.error?.description || "Transaction declined";
        toast.error(`Payment Failed: ${reason}`);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to initiate payment");
    }
  };

  return (
    <div className="pb-28 px-4 pt-4" data-testid="pricing-page">
      <BackButton className="mb-2" />
      <div className="text-center max-w-md mx-auto">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-orange-600">Pricing</div>
        <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Grow your business with IIP</h1>
        <p className="text-sm text-slate-600 mt-2">Pick a plan that fits your scale. Upgrade anytime.</p>

        <div className="mt-4 inline-flex bg-slate-100 rounded-full p-1 text-xs font-semibold" data-testid="billing-toggle">
          {["monthly", "yearly"].map((b) => (
            <button key={b} onClick={() => setBilling(b)} data-testid={`billing-${b}`}
              className={`px-4 py-1.5 rounded-full transition-colors ${billing === b ? "bg-white text-blue-800 shadow-sm" : "text-slate-600"}`}>
              {b === "yearly" ? "Yearly · save 17%" : "Monthly"}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {plans.map((p) => {
          const c = COLOR_MAP[p.color] || COLOR_MAP.blue;
          const Icon = ICONS[p.color] || Zap;
          const basePrice = billing === "yearly" ? p.yearly_price : p.monthly_price;
          const gstAmount = Math.round(basePrice * 0.18);
          const totalPrice = basePrice + gstAmount;
          const cad = billing === "yearly" ? "/year" : "/month";
          return (
            <div key={p.id} className={`relative bg-white rounded-2xl p-5 border ${c.ring} shadow-sm hover:shadow-md transition-all`} data-testid={`plan-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
              {p.badge && (
                <span className={`absolute -top-2 right-4 text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full ${c.badge}`}>
                  {p.badge}
                </span>
              )}
              <div className="flex items-center gap-2">
                <Icon className={c.icon} />
                <div className="font-display text-2xl font-bold text-slate-900">{p.name}</div>
              </div>
              {p.description && <p className="text-xs text-slate-500 mt-1">{p.description}</p>}

              {/* Highlights: Lead Unlocks and Company Website */}
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-100 text-slate-800 border border-slate-200">
                  <Sparkles size={12} className="text-orange-500" />
                  {p.unlocks_per_month === null || p.unlocks_per_month === undefined
                    ? "Unlimited Lead Unlocks"
                    : `${p.unlocks_per_month} Lead Unlocks / mo`}
                </span>
                {p.has_website ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-blue-50 text-blue-800 border border-blue-200">
                    <Check size={12} className="text-blue-600" />
                    Company Website Included
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-slate-50 text-slate-400 border border-slate-200 line-through">
                    Company Website
                  </span>
                )}
              </div>

              <div className="mt-3 space-y-0.5">
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-3xl font-bold text-slate-900">
                    {basePrice > 0 ? `₹${totalPrice.toLocaleString("en-IN")}` : (p.name === "Enterprise" ? "Custom" : "₹0")}
                  </span>
                  {basePrice > 0 && <span className="text-xs text-slate-500">{cad} (incl. 18% GST)</span>}
                </div>
                {basePrice > 0 && (
                  <div className="text-[11px] text-slate-500 font-medium">
                    Base: ₹{basePrice.toLocaleString("en-IN")} + ₹{gstAmount.toLocaleString("en-IN")} (18% GST)
                  </div>
                )}
              </div>
              <ul className="mt-3 space-y-1.5">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => choose(p)} data-testid={`plan-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`mt-4 w-full py-3 rounded-full font-semibold transition-colors ${c.btn}`}>
                {p.monthly_price === 0 && p.name !== "Enterprise" ? "Current Plan" : (p.name === "Enterprise" ? "Talk to sales" : "Choose plan")}
              </button>
            </div>
          );
        })}
      </div>

      {/* Post-Payment Tax Invoice Success Modal */}
      {invoiceModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 relative">
            <button
              onClick={() => {
                setInvoiceModal({ open: false, invoiceNumber: "", downloadUrl: "", planName: "" });
                window.location.reload();
              }}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
            >
              <X size={18} />
            </button>

            <div className="text-center pt-2">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600">
                <CheckCircle2 size={36} />
              </div>

              <span className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                Payment Successful
              </span>

              <h2 className="font-display text-2xl font-black text-slate-900 mt-3">
                Subscription Confirmed!
              </h2>

              <p className="text-sm text-slate-600 mt-1">
                You are now subscribed to the <strong>{invoiceModal.planName} Plan</strong>. An official GST Tax Invoice has been generated and emailed to your registered address.
              </p>

              <div className="mt-5 p-4 bg-slate-50 border border-slate-200 rounded-2xl text-left space-y-1.5">
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Document Type:</span>
                  <span className="font-bold text-slate-700">Tax Invoice</span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Invoice Number:</span>
                  <span className="font-mono font-bold text-blue-900">{invoiceModal.invoiceNumber}</span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>GST Rate:</span>
                  <span className="font-bold text-slate-700">18% (SAC 9983)</span>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-2.5">
                <a
                  href={invoiceModal.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={`Tax_Invoice_${invoiceModal.invoiceNumber}.pdf`}
                  className="w-full py-3.5 px-4 rounded-full bg-blue-900 text-white font-bold text-sm hover:bg-blue-950 transition-all shadow-md shadow-blue-950/20 flex items-center justify-center gap-2"
                >
                  <Download size={18} /> Download Tax Invoice (PDF)
                </a>

                <button
                  onClick={() => {
                    setInvoiceModal({ open: false, invoiceNumber: "", downloadUrl: "", planName: "" });
                    window.location.reload();
                  }}
                  className="w-full py-2.5 px-4 rounded-full bg-slate-100 text-slate-700 font-semibold text-xs hover:bg-slate-200 transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
